/**
 * Canonical role → live-environment routing for the standalone app.
 *
 * SINGLE SOURCE OF TRUTH (frontend side). Must stay in sync with the Laravel
 * `App\Support\Live\LiveEnvironmentResolver` on tippelstreet.com so a role is
 * always sent to exactly one environment from both sides:
 *
 *   operator            → /operator   (live console; office self-seat lands here too)
 *   escort / performer  → /escort     (provider chat)
 *   office              → (no live env — managed on the tenant frontend)
 *   customer / default  → /chat       (customer chat)
 *
 * The gateway user payload carries `roles` (array of tenant + global role names)
 * and `account_type` ('customer'|'escort'|'performer'|'office', never operator),
 * but NO singular `role` field — routing is derived from those two.
 */

const ENV_HOME = {
  customer: '/chat',
  provider: '/escort',
  operator: '/operator',
};

const ENV_FOR_BASE_PATH = {
  '/chat': 'customer',
  '/escort': 'provider',
  '/operator': 'operator',
};

function roleList(user) {
  if (!user || !Array.isArray(user.roles)) return [];
  return user.roles.map((r) => String(r).toLowerCase());
}

/**
 * Resolve the user's environment class: 'operator' | 'provider' | 'office' | 'customer'.
 * Returns null when no user is provided.
 */
export function resolveEnvironment(user) {
  if (!user) return null;

  const roles = roleList(user);
  const accountType =
    typeof user.account_type === 'string' ? user.account_type.toLowerCase() : '';

  // Operator wins for the live environment — an office owner who granted
  // themselves an operator seat also belongs in the operator console.
  if (roles.includes('operator')) return 'operator';

  if (
    accountType === 'escort' ||
    accountType === 'performer' ||
    roles.includes('escort') ||
    roles.includes('performer')
  ) {
    return 'provider';
  }

  // Office is management-only: it has no live chat environment.
  if (accountType === 'office' || roles.includes('office')) return 'office';

  return 'customer';
}

/**
 * The role's home path inside the standalone app, or null when the role has no
 * live environment (office).
 */
export function resolveRoleHome(user) {
  const env = resolveEnvironment(user);
  if (!env || env === 'office') return null;
  return ENV_HOME[env] ?? '/chat';
}

function basePath(path) {
  if (!path || typeof path !== 'string') return '';
  const q = path.indexOf('?');
  const p = q >= 0 ? path.slice(0, q) : path;
  const trimmed = p.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * Choose a safe post-auth destination for the user.
 *
 * A requested `next` is honoured ONLY when its base path belongs to the user's
 * own environment (so deep links like `/chat?participant_id=…` keep working for
 * customers). Any cross-environment or unknown target (including the retired
 * `/rooms` demo) falls back to the role home, so a role can never be deep-linked
 * into a foreign surface.
 *
 * Returns `fallback` (default null) when the user has no live environment
 * (office) so the caller can bounce them back to the tenant site.
 */
export function pickSafeDestination(user, requestedNext, fallback = null) {
  const home = resolveRoleHome(user);
  if (!home) return fallback;

  const isInternal =
    typeof requestedNext === 'string' &&
    requestedNext.startsWith('/') &&
    !requestedNext.startsWith('//');

  if (!isInternal) return home;

  const env = resolveEnvironment(user);
  const target = ENV_FOR_BASE_PATH[basePath(requestedNext)];

  return target && target === env ? requestedNext : home;
}

// ─────────────────────────────────────────────────────────────────────────────
// Canonical Live Action Contract (MUST stay in sync with Laravel
// App\Support\Live\LiveEnvironmentResolver)
//
// Provides a single source of truth for:
//   - surface: 'provider' | 'customer' | 'operator' | null (office)
//   - next   : the value to put in ?next= when calling tenant handoff
//   - label  : i18n key or fallback English label for the primary CTA
// ─────────────────────────────────────────────────────────────────────────────

const LIVE_ACTION = {
  provider:  { next: '/escort',  label: 'Go Live' },
  customer:  { next: '/chat',    label: 'Open Chat' },
  operator:  { next: '/operator', label: 'Open Console' },
};

/**
 * Canonical live-action descriptor.
 * Returns null for users with no live environment (office).
 */
export function getLiveAction(user, participantId = null) {
  const env = resolveEnvironment(user);
  if (!env || env === 'office') return null;

  const base = LIVE_ACTION[env];
  if (!base) return null;

  let next = base.next;
  if (env === 'customer' && participantId && Number(participantId) > 0) {
    next = `/chat?participant_id=${participantId}`;
  }

  return {
    surface: env,
    next,
    label: base.label,
  };
}

/**
 * Convenience: human label for the primary live CTA.
 */
export function getLiveCtaLabel(user) {
  const action = getLiveAction(user);
  return action ? action.label : null;
}

/**
 * Convenience: the `next` value for the primary live CTA.
 * Returns null for office.
 */
export function getLiveCtaNext(user, participantId = null) {
  const action = getLiveAction(user, participantId);
  return action ? action.next : null;
}
